// src/store/tourDataSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient"; // centralized axios instance

const ENDPOINT = "/tourData/";

// Fetch all tour records
export const fetchTourData = createAsyncThunk(
  "tourData/fetchTourData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(ENDPOINT);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Insert a new tour record
export const insertTourData = createAsyncThunk(
  "tourData/insertTourData",
  async (tour, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("tc_id", tour.tc_id);
      formData.append("tourDesc", tour.tourDesc);
      formData.append("tourSDate", tour.tourSDate);
      formData.append("tourEDate", tour.tourEDate);
      formData.append("tourCity", tour.tourCity);
      formData.append("tourCountry", tour.tourCountry);
      formData.append("tourCost", tour.tourCost);
      formData.append("countOfPerson", tour.countOfPerson);
      formData.append("status", "A");

      if (tour.tourBill instanceof File) {
        formData.append("tourBill", tour.tourBill);
      }

      const response = await apiClient.post(ENDPOINT, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update an existing tour record
export const updateTourData = createAsyncThunk(
  "tourData/updateTourData",
  async (tour, { rejectWithValue }) => {
    try {
      const { id } = tour;
      if (!id) throw new Error("Missing tour id for update");

      const formData = new FormData();
      formData.append("tc_id", tour.tc_id);
      formData.append("tourDesc", tour.tourDesc);
      formData.append("tourSDate", tour.tourSDate);
      formData.append("tourEDate", tour.tourEDate);
      formData.append("tourCity", tour.tourCity);
      formData.append("tourCountry", tour.tourCountry);
      formData.append("tourCost", tour.tourCost);
      formData.append("countOfPerson", tour.countOfPerson);
      formData.append("status", tour.status || "A");

      if (tour.tourBill instanceof File) {
        formData.append("tourBill", tour.tourBill);
      }

      const response = await apiClient.put(`${ENDPOINT}${id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete a tour record
export const deleteTourData = createAsyncThunk(
  "tourData/deleteTourData",
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`${ENDPOINT}${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice definition
const tourDataSlice = createSlice({
  name: "tourData",
  initialState: {
    tourData: [],
    loading: false,
    error: null,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTourData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTourData.fulfilled, (state, action) => {
        state.loading = false;
        state.tourData = action.payload;
      })
      .addCase(fetchTourData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(insertTourData.fulfilled, (state, action) => {
        state.tourData.push(action.payload);
      })
      .addCase(insertTourData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateTourData.fulfilled, (state, action) => {
        const index = state.tourData.findIndex((td) => td.id === action.payload.id);
        if (index !== -1) {
          state.tourData[index] = action.payload;
        }
      })
      .addCase(updateTourData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteTourData.fulfilled, (state, action) => {
        state.tourData = state.tourData.filter((td) => td.id !== action.payload);
      })
      .addCase(deleteTourData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default tourDataSlice.reducer;
